/**
 * Access page selectors — pure functions, unit-testable in isolation.
 */

export const ROLES = ["OWNER", "ADMIN", "DEVELOPER", "VIEWER", "BILLING", "SYSTEM"];

/**
 * deriveDisplayRole(member, memberList, currentUser) -> one of ROLES
 *
 * The backend Member record today only carries ACCOUNT_ADMIN | MEMBER.
 * The UI wants 6 pill variants (SCK-462 policy matrix). Fabricate
 * deterministically until the backend adds a real role enum on
 * AccountMemberRecord.Member (see coord scout gap #4 in the design).
 *
 * Mapping:
 *   OWNER      = first ACCOUNT_ADMIN by grantedOn ASC
 *   ADMIN      = other ACCOUNT_ADMIN
 *   DEVELOPER  = MEMBER with canCreateProjects=true
 *   VIEWER     = MEMBER with canCreateProjects=false
 *   BILLING    = (never in v1 — no backend signal)
 *   SYSTEM     = currentUser.userType==='SYSTEM' — only overlays the current user
 */
export function deriveDisplayRole(member, memberList, currentUser) {
  if (!member) return "VIEWER";

  if (
    currentUser &&
    currentUser.userType === "SYSTEM" &&
    (currentUser.id === member.userId || currentUser.userId === member.userId)
  ) {
    return "SYSTEM";
  }

  if (member.role === "ACCOUNT_ADMIN") {
    // Deterministic OWNER = first ADMIN by grantedOn ascending.
    const admins = (memberList || [])
      .filter((m) => m.role === "ACCOUNT_ADMIN")
      .slice()
      .sort((a, b) => {
        const ta = a.grantedOn ? Date.parse(a.grantedOn) : Number.MAX_SAFE_INTEGER;
        const tb = b.grantedOn ? Date.parse(b.grantedOn) : Number.MAX_SAFE_INTEGER;
        return ta - tb;
      });
    if (admins[0] && (admins[0].userId === member.userId)) return "OWNER";
    return "ADMIN";
  }

  return member.canCreateProjects ? "DEVELOPER" : "VIEWER";
}

/**
 * filterMembers(query, members) — case-insensitive substring across
 * email + givenName + familyName + userId.
 */
export function filterMembers(query, members) {
  if (!query || !query.trim()) return members || [];
  const q = query.trim().toLowerCase();
  return (members || []).filter((m) => {
    const bag = [
      m.email || "",
      m.givenName || "",
      m.familyName || "",
      m.userId || "",
    ].join(" ").toLowerCase();
    return bag.includes(q);
  });
}

/**
 * displayName(member) — "First Last" or fallback to email local-part.
 */
export function displayName(member) {
  if (!member) return "";
  const g = (member.givenName || "").trim();
  const f = (member.familyName || "").trim();
  const full = [g, f].filter(Boolean).join(" ");
  if (full) return full;
  const email = member.email || "";
  return email.includes("@") ? email.split("@")[0] : email;
}

/**
 * initialsOf(member) — "AR" or first char of email.
 */
export function initialsOf(member) {
  if (!member) return "?";
  const g = (member.givenName || "").trim();
  const f = (member.familyName || "").trim();
  if (g || f) return `${g.charAt(0)}${f.charAt(0)}`.toUpperCase() || "?";
  const email = member.email || "";
  return (email.charAt(0) || "?").toUpperCase();
}

/**
 * stringToColor(str) — deterministic HSL from a string hash.
 * Used for avatar backgrounds so identical inputs paint the same color.
 */
export function stringToColor(str) {
  const s = str || "";
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(h) % 360;
  return `hsl(${hue}, 55%, 45%)`;
}
